import type { LinhaSimuladaResponse, RestricaoLinhaFicha } from '@triade/contracts';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ROTULO_ALERTA, ROTULO_AVISO_LINHA, type LinhaFicha } from './tipos';

const ROTULO_RESTRICAO: Record<RestricaoLinhaFicha, string> = {
  MAO_DE_OBRA_FORA_DO_PROCESSO: 'Mão de obra fora do processo',
  COMPONENTE_NAO_PERMITIDO: 'Item não compõe ficha',
  CENTRO_CONSUMO_FORA_DAS_ETAPAS: 'Centro de consumo sem etapa',
};

/** O que fazer por codigo (alerta, aviso ou restricao da API). Codigo fora deste mapa: mostra o codigo e "Revise a linha". */
export const O_QUE_FAZER: Readonly<Record<string, string>> = {
  CPM_AUSENTE: 'Lance o CPM do centro em Custos › Centros de custo',
  CPM_ANTIGO: 'O CPM do mês corrente não foi salvo: salve o rateio do mês',
  CUSTO_AUSENTE: 'Cadastre o custo de aquisição do insumo',
  AQUISICAO_AUSENTE: 'Cadastre o custo de aquisição do insumo',
  MAO_DE_OBRA_FORA_DO_PROCESSO: 'Mão de obra do produto vai em Processo produtivo, com centro e minutos',
  ETAPA_TIPO_INVALIDO: 'Escolha um centro analítico de transformação',
  CENTRO_INVALIDO: 'Escolha um centro analítico de transformação',
  CENTRO_CONSUMO_FORA_DAS_ETAPAS: 'Inclua a etapa desse centro no Processo produtivo ou escolha um centro que já esteja nas etapas',
  CUSTO_CONGELADO_SEMIACABADO: 'Escolha uma ficha para o semiacabado (Fichas técnicas) e revise o preço do kit',
};

const REVISE_A_LINHA = 'Revise a linha';

/** Uma linha da lista: uma por item (alerta, aviso ou restricao) de cada linha da ficha. */
export interface ItemAtencao {
  uid: string;
  descricao: string;
  /** Codigo da API (ex.: CPM_AUSENTE) e o rotulo que o explica. */
  codigo: string;
  motivo: string;
  oQueFazer: string;
}

/**
 * Monta a lista de atencao a partir do que a API devolveu na simulacao (alertas, avisos e restricoes por linha).
 * Nao calcula nada: so junta os codigos com a descricao da linha e o texto de orientacao.
 */
export function itensDeAtencao(linhas: ReadonlyArray<LinhaFicha>, calculo: ReadonlyMap<string, LinhaSimuladaResponse>): ItemAtencao[] {
  return linhas.flatMap((l) => {
    const sim = calculo.get(l.uid);
    if (!sim) return [];
    const codigos: Array<{ codigo: string; motivo: string }> = [
      ...sim.alertas.map((c) => ({ codigo: c, motivo: ROTULO_ALERTA[c] })),
      ...(sim.avisos ?? []).map((c) => ({ codigo: c, motivo: ROTULO_AVISO_LINHA[c] })),
      ...(sim.restricoes ?? []).map((c) => ({ codigo: c, motivo: ROTULO_RESTRICAO[c] })),
    ];
    const descricao = l.descricao.trim() || 'Linha sem descrição';
    return codigos.map(({ codigo, motivo }) => {
      const conhecido = O_QUE_FAZER[codigo];
      return {
        uid: l.uid,
        descricao,
        codigo,
        // Codigo que o front nao conhece (rotulo ausente): o proprio codigo aparece no motivo.
        motivo: motivo ?? codigo,
        oQueFazer: conhecido ?? REVISE_A_LINHA,
      };
    });
  });
}

/** Lista "Linhas que precisam de atenção": uma linha por item, com o motivo e o que fazer. */
export function ListaAtencao({ itens, aberta, onAbertaChange }: { itens: ReadonlyArray<ItemAtencao>; aberta: boolean; onAbertaChange: (aberta: boolean) => void }) {
  return (
    <Dialog open={aberta} onOpenChange={onAbertaChange}>
      <DialogContent className="sm:max-w-3xl" data-testid="lista-atencao">
        <DialogHeader>
          <DialogTitle>Linhas que precisam de atenção</DialogTitle>
          <DialogDescription>Motivos que a API apontou nas linhas da ficha. Corrija na grade e salve de novo.</DialogDescription>
        </DialogHeader>
        {itens.length === 0 ? (
          <p role="status" className="text-small text-ink-muted">
            Nenhuma linha precisa de atenção.
          </p>
        ) : (
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-left text-small">
              <thead className="text-caption text-ink-muted">
                <tr>
                  <th scope="col" className="py-2 pr-3 font-medium">Linha</th>
                  <th scope="col" className="py-2 pr-3 font-medium">Motivo</th>
                  <th scope="col" className="py-2 font-medium">O que fazer</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((i) => (
                  <tr key={`${i.uid}-${i.codigo}`} className="border-t border-border align-top" data-testid="item-atencao">
                    <td className="py-2 pr-3 font-medium text-ink">{i.descricao}</td>
                    <td className="py-2 pr-3">
                      <span className="block">{i.motivo}</span>
                      <span className="block text-caption text-ink-muted">{i.codigo}</span>
                    </td>
                    <td className="py-2 text-ink-secondary">{i.oQueFazer}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
